import React from 'react';
import { Box, ButtonBase, Stack, Typography, Fade } from '@mui/material';
import { getAccentColor, getBodyColor } from './shared';

// Custom Sleek Arrow Icon
const ArrowRightIcon = ({ sx, className }) => (
  <Box component="svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" sx={{ width: 18, height: 18, ...sx }} className={className}>
    <line x1="5" y1="12" x2="19" y2="12"></line>
    <polyline points="12 5 19 12 12 19"></polyline>
  </Box>
);

const SplitMediaStorySection = ({
  section,
  sectionKey,
  sectionIndex,
  mediaHeight,
  bodyContent,
  canCollapse,
  isExpanded,
  readMoreLabel,
  readLessLabel,
  onToggleExpand,
  renderPositionableElement,
}) => {
  const accent = getAccentColor(section, '#1F3C88');
  const bodyColor = getBodyColor(section) || 'text.secondary';
  const imageOnLeft = section.imagePosition !== 'right';

  return (
    <Box
      sx={{
        my: { xs: 0.8, md: 1.4 }, 
        position: 'relative',
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gap: { xs: 1.4, md: 2.4, lg: 3.2 }, 
          // FIX 1: Balanced the grid. It's now roughly a 40/60 split instead of almost 50/50
          gridTemplateColumns: { 
            xs: '1fr', 
            md: imageOnLeft ? '0.7fr 1.3fr' : '1.3fr 0.7fr' 
          },
          alignItems: 'center',
          width: '100%',
        }}
      >
        {/* ========================================== */}
        {/* MEDIA COLUMN                               */}
        {/* ========================================== */}
        <Box sx={{ order: { xs: 1, md: imageOnLeft ? 1 : 2 }, position: 'relative', width: '100%' }}>
          
          {/* Ambient Glow */}
          <Box
            sx={{
              position: 'absolute',
              top: '10%',
              left: imageOnLeft ? '-5%' : '5%',
              width: '100%',
              height: '80%',
              background: `radial-gradient(circle, ${accent}30 0%, rgba(255,255,255,0) 70%)`,
              filter: 'blur(40px)',
              zIndex: 0,
              pointerEvents: 'none',
              borderRadius: '50%',
            }}
          />

          {renderPositionableElement({
            section,
            index: sectionIndex,
            elementKey: `${sectionKey}-story-image`,
            children: section.image ? (
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  width: '100%',
                  // FIX 3: Changed aspect ratio to something more standard, and capped the max height
                  aspectRatio: { xs: '1 / 1', sm: '4 / 3', md: '3 / 4' }, 
                  maxHeight: { md: 540 }, // Prevents massive vertical stretching on desktop
                  borderRadius: '6px', // Slightly softer curve
                  overflow: 'hidden',
                  boxShadow: '0 20px 40px -10px rgba(0,0,0,0.1)',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '6px',
                    border: '1px solid rgba(0,0,0,0.05)', // Dark mode friendly inner border
                    pointerEvents: 'none',
                  },
                  transition: 'transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 0.4s ease',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: '0 24px 50px -12px rgba(0,0,0,0.15)',
                  },
                  '&:hover .story-img': {
                    transform: 'scale(1.04)',
                  }
                }}
              >
                <Box
                  className="story-img"
                  component="img"
                  src={section.image}
                  alt={section.title || 'Story Image'}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: section.imageObjectPosition || 'center center',
                    transition: 'transform 0.6s cubic-bezier(0.2, 0.8, 0.2, 1)',
                  }}
                />
              </Box>
            ) : (
              // Empty State
              <Box 
                sx={{ 
                  aspectRatio: { xs: '1 / 1', sm: '4 / 3', md: '3 / 4' },
                  maxHeight: { md: 540 },
                  borderRadius: '6px',
                  border: '2px dashed rgba(148,163,184,0.3)',
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(248,250,252,0.5)',
                }}
              >
                <Typography sx={{ color: '#94A3B8', fontSize: '0.85rem', fontWeight: 600, textTransform: 'uppercase' }}>
                  Add Image
                </Typography>
              </Box>
            ),
          })}
        </Box>

        {/* ========================================== */}
        {/* CONTENT COLUMN                             */}
        {/* ========================================== */}
        <Box sx={{ order: { xs: 2, md: imageOnLeft ? 2 : 1 }, zIndex: 1 }}>
          <Fade in={true} timeout={800}>
            <Stack 
              spacing={{ xs: 2, md: 3 }} 
              sx={{ 
                width: '100%', 
                maxWidth: 600, // Constrains line-length for optimal readability
                mx: { xs: 'auto', md: 0 }, // Center on mobile, align to grid on desktop
              }}
            >
              
              {/* Eyebrow / Pre-Title */}
              {section.buttonLabel ? (
                <Typography
                  component="div"
                  sx={{
                    color: accent,
                    fontWeight: 800,
                    fontSize: { xs: '0.75rem', md: '0.8rem' },
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ width: 24, height: 2, background: accent, borderRadius: 1 }} />
                  {section.buttonLabel}
                </Typography>
              ) : null}

              {/* Main Title */}
              {section.title ? (
                <Typography
                  variant="h2"
                  sx={{
                    color: 'text.primary',
                    fontWeight: 900,
                    fontSize: { xs: '2rem', sm: '2.4rem', md: '3rem' }, // Slightly reduced size
                    lineHeight: 1.15,
                    letterSpacing: '-0.02em',
                  }}
                >
                  {section.title}
                </Typography>
              ) : null}

              {/* Body Content */}
              <Box 
                sx={{ 
                  color: bodyColor, 
                  fontSize: { xs: '1rem', md: '1.05rem' }, // Scaled down text slightly to match new image size
                  lineHeight: 1.7,
                  fontWeight: 400,
                  '& p': { mt: 0, mb: 2 },
                  '& p:last-child': { mb: 0 }
                }}
              >
                {bodyContent || 'Add content in the editor to see your story come to life.'}
              </Box>

              {/* Expand / Collapse Action */}
              {canCollapse ? (
                <Box sx={{ pt: 1 }}>
                  <ButtonBase
                    onClick={onToggleExpand}
                    disableRipple
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      color: accent,
                      fontWeight: 700,
                      fontSize: '1rem',
                      pb: 0.5,
                      borderBottom: `2px solid transparent`,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderBottom: `2px solid ${accent}`,
                      },
                      '&:hover .action-arrow': {
                        transform: 'translateX(4px)',
                      }
                    }}
                  >
                    {isExpanded ? readLessLabel : readMoreLabel}
                    <ArrowRightIcon 
                      className="action-arrow"
                      sx={{ 
                        transition: 'transform 0.2s ease',
                        transform: isExpanded ? 'rotate(-180deg)' : 'none' 
                      }} 
                    />
                  </ButtonBase>
                </Box>
              ) : null}

            </Stack>
          </Fade>
        </Box>
      </Box>
    </Box>
  );
};

export default SplitMediaStorySection;
